import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Seo } from "./components/site/Seo";
import { PageHero } from "./components/site/PageHero";
import { Reveal, Overline, IMG } from "./components/site/shared";
import { ZoneIntervention } from "./components/site/ZoneIntervention";
import { Related } from "./components/site/Related";
import { Ppe } from "./components/site/Ppe";
import { Travaux } from "./components/site/Travaux";
import { About } from "./components/site/About";
import { Realisations } from "./components/site/Realisations";
import { DevisForm } from "./components/site/DevisForm";

const Section = ({ title, children, brume = false }) => (
  <section className={`${brume ? "bg-brume" : "bg-white"} py-16 lg:py-24`}>
    <div className="mx-auto max-w-7xl px-5 lg:px-10">
      <Reveal>
        <h2 className="font-display max-w-3xl text-2xl font-medium leading-tight tracking-tight text-nuit sm:text-4xl">{title}</h2>
      </Reveal>
      {children}
    </div>
  </section>
);

const List = ({ items }) => (
  <ul className="space-y-3">
    {items.map((i) => (
      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-nuit/70 sm:text-base">
        <Check className="mt-1 h-4 w-4 shrink-0 text-lagoon" /> {i}
      </li>
    ))}
  </ul>
);

const SubCard = ({ to, title, desc }) => (
  <Link to={to} data-testid={`subcard-${to.slice(1)}`} className="group flex items-start justify-between gap-4 border border-nuit/10 bg-white p-6 transition-colors duration-300 hover:border-lagoon">
    <div>
      <p className="font-display text-lg font-medium text-nuit">{title}</p>
      <p className="mt-1 text-sm text-nuit/55">{desc}</p>
    </div>
    <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-nuit/25 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-lagoon" />
  </Link>
);

export const EntretienPpePage = () => (
  <>
    <Seo
      title="Entretien d'immeubles & conciergerie PPE à Sion et en Valais | Rosset Conciergerie"
      description="Service de conciergerie et entretien d'immeubles et PPE à Sion et en Valais central : halls, escaliers, zones poubelles, contrôle visuel et signalement. Devis gratuit."
      path="/entretien-immeubles-ppe"
    />
    <PageHero
      overline="Conciergerie d'immeubles"
      title="Entretien d'immeubles et conciergerie PPE à Sion et en Valais"
      intro="Rosset Conciergerie & Services accompagne les PPE, les régies immobilières et les propriétaires de Sion et du Valais central avec un service de conciergerie régulier, soigné et facile à joindre."
    />
    <Ppe />
    <ZoneIntervention />
    <Related current="/entretien-immeubles-ppe" />
  </>
);

export const NettoyageProfessionnelPage = () => (
  <>
    <Seo
      title="Entreprise de nettoyage à Sion — nettoyage professionnel en Valais"
      description="Entreprise de nettoyage à Sion : nettoyage régulier ou ponctuel, fin de bail, fin de chantier et interventions sur mesure pour PPE, régies, professionnels et particuliers en Valais."
      path="/nettoyage-professionnel"
    />
    <PageHero
      overline="Nettoyage professionnel"
      title="Entreprise de nettoyage à Sion, au service des immeubles du Valais"
      intro="Nettoyage régulier ou ponctuel, fin de bail, fin de chantier ou besoin particulier : chaque intervention est définie selon votre situation, avec un devis clair et gratuit."
    />
    <Section title="Un nettoyage adapté à chaque situation">
      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <Reveal>
          <p className="text-base leading-relaxed text-nuit/70">
            Un hall d'immeuble fréquenté chaque jour, un logement à remettre proprement, un chantier
            qui se termine ou un local professionnel à entretenir : les besoins ne se ressemblent pas.
            Rosset Conciergerie &amp; Services propose des passages réguliers comme des interventions
            ponctuelles, à Sion et dans tout le Valais central.
          </p>
          <p className="mt-4 text-base leading-relaxed text-nuit/70">
            Chaque mandat commence par une visite ou un échange précis sur vos attentes, puis fait
            l'objet d'un devis gratuit et sans engagement.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="grid grid-cols-1 gap-4">
            <SubCard to="/nettoyage-fin-de-bail" title="Nettoyage fin de bail" desc="Remise en état du logement avant restitution" />
            <SubCard to="/nettoyage-fin-de-chantier" title="Nettoyage fin de chantier" desc="Après construction ou rénovation" />
            <SubCard to="/nettoyage-haute-pression" title="Nettoyage haute pression" desc="Terrasses, dallages, murs et garages" />
          </div>
        </Reveal>
      </div>
    </Section>
    <Section title="Pour les PPE, les régies, les professionnels et les particuliers" brume>
      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <Reveal>
          <List items={[
            "Parties communes d'immeubles et de PPE",
            "Locaux professionnels et surfaces commerciales",
            "Logements avant remise ou après départ",
            "Interventions ponctuelles selon vos besoins",
          ]} />
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-base leading-relaxed text-nuit/70">
            Basée à Sion, l'entreprise intervient rapidement sur le Valais central et privilégie
            une relation directe : vous parlez à la personne qui effectue le travail.
          </p>
        </Reveal>
      </div>
    </Section>
    <Related current="/nettoyage-professionnel" />
  </>
);

export const NettoyageHautePressionPage = () => (
  <>
    <Seo
      title="Nettoyage haute pression à Sion et en Valais — terrasses, dallages, murs"
      description="Nettoyage haute pression en Valais : terrasses, dallages, pavés, murs extérieurs, escaliers, sols de garages et parkings. Redonnez un coup de jeune à vos surfaces. Devis gratuit."
      path="/nettoyage-haute-pression"
    />
    <PageHero
      overline="Nettoyages spéciaux"
      title="Nettoyage haute pression à Sion et en Valais"
      intro="Terrasses, dallages, murs extérieurs ou sols de garages : un nettoyage professionnel adapté au support peut transformer l'apparence d'une surface, sans remplacement coûteux."
    />
    <Section title="Redonner un coup de jeune à vos surfaces extérieures">
      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <Reveal>
          <p className="text-base leading-relaxed text-nuit/70">
            Avec le temps, les intempéries et la fréquentation, les surfaces extérieures accumulent
            salissures, mousses et dépôts. Le nettoyage haute pression, adapté à la nature de chaque
            support, permet de retrouver des surfaces propres et accueillantes — terrasses, dallages
            et pavés, entrées d'immeubles, murets, escaliers extérieurs, cours et accès.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Terrasses", "Dallages", "Pavés", "Murs compatibles", "Murets", "Escaliers extérieurs", "Cours & accès", "Parkings"].map((s) => (
              <span key={s} className="border border-nuit/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-petrole">{s}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="overflow-hidden">
            <img src={IMG.pression} alt="Nettoyage haute pression d'une toiture vitrée en Valais" loading="lazy" className="aspect-[4/3] w-full object-cover" />
          </div>
        </Reveal>
      </div>
    </Section>
    <Section title="Sols de garages et parkings souterrains" brume>
      <div className="mt-8 max-w-3xl">
        <Reveal>
          <p className="text-base leading-relaxed text-nuit/70">
            Poussières, traces de pneus et salissures s'accumulent dans les garages souterrains des
            immeubles et des PPE. Rosset Conciergerie &amp; Services propose le nettoyage ponctuel ou
            périodique des sols, des zones de circulation, des places de stationnement et des rampes
            d'accès, selon les possibilités techniques du site.
          </p>
        </Reveal>
      </div>
    </Section>
    <Related current="/nettoyage-haute-pression" />
  </>
);

export const NettoyageFinDeBailPage = () => (
  <>
    <Seo
      title="Nettoyage fin de bail à Sion et en Valais | Rosset Conciergerie & Services"
      description="Nettoyage fin de bail à Sion et en Valais central : remise en état complète du logement avant restitution — cuisine, sanitaires, sols, vitres. Devis gratuit et rapide."
      path="/nettoyage-fin-de-bail"
    />
    <PageHero
      overline="Nettoyage fin de bail"
      title="Nettoyage fin de bail à Sion et en Valais central"
      intro="Un déménagement laisse peu de temps pour tout faire. Confiez la remise en état du logement à un professionnel et abordez l'état des lieux sereinement."
    />
    <Section title="Un logement prêt à être restitué">
      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <Reveal>
          <p className="text-base leading-relaxed text-nuit/70">
            Le nettoyage de fin de bail vise à restituer le logement dans l'état attendu par le
            propriétaire ou la régie. L'intervention est définie selon le logement et ses exigences,
            puis confirmée par un devis gratuit.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <List items={[
            "Cuisine et appareils encastrés accessibles",
            "Sanitaires, faïences et robinetteries",
            "Sols, plinthes et surfaces",
            "Vitres, cadres et portes vitrées",
            "Caves et espaces annexes selon convenance",
          ]} />
        </Reveal>
      </div>
    </Section>
    <Related current="/nettoyage-fin-de-bail" />
  </>
);

export const NettoyageFinDeChantierPage = () => (
  <>
    <Seo
      title="Nettoyage fin de chantier en Valais — après construction ou rénovation"
      description="Nettoyage de fin de chantier à Sion et en Valais : élimination des poussières, nettoyage des sols et des vitres, préparation des locaux avant remise. Devis gratuit."
      path="/nettoyage-fin-de-chantier"
    />
    <PageHero
      overline="Nettoyage fin de chantier"
      title="Nettoyage de fin de chantier à Sion et en Valais"
      intro="Après une construction ou une rénovation, un nettoyage méticuleux prépare les lieux à la remise au propriétaire ou à l'arrivée des occupants."
    />
    <Section title="Des locaux prêts pour la remise">
      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <Reveal>
          <p className="text-base leading-relaxed text-nuit/70">
            Poussières fines, traces de matériaux, résidus légers : la fin d'un chantier demande un
            nettoyage méthodique. L'intervention est planifiée avec l'entreprise ou le maître
            d'ouvrage pour s'intégrer au calendrier de remise.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <List items={[
            "Élimination des poussières de chantier",
            "Nettoyage des sols et des surfaces",
            "Vitres, cadres et menuiseries",
            "Évacuation des résidus légers de chantier",
            "Préparation des locaux avant remise",
          ]} />
        </Reveal>
      </div>
    </Section>
    <Related current="/nettoyage-fin-de-chantier" />
  </>
);

export const TravauxEntretienPage = () => (
  <>
    <Seo
      title="Petits & moyens travaux d'entretien à Sion et en Valais | Rosset Conciergerie"
      description="Petites réparations, maintenance immobilière, rafraîchissement et remise en état à Sion et en Valais : un interlocuteur polyvalent au service de l'entretien de votre bien."
      path="/travaux-entretien"
    />
    <PageHero
      overline="Petits & moyens travaux"
      title="Petits et moyens travaux d'entretien à Sion et en Valais"
      intro="Réparations, maintenance, rafraîchissement ou remise en état : profitez d'un interlocuteur polyvalent au service de l'entretien de votre bien, sans chercher un nouvel artisan pour chaque intervention."
    />
    <Travaux />
    <Related current="/travaux-entretien" />
  </>
);

export const RealisationsPage = () => (
  <>
    <Seo
      title="Réalisations en Valais — nettoyage, rénovation et extérieurs | Rosset Conciergerie"
      description="Réalisations de Rosset Conciergerie & Services en Valais : entretien d'immeubles, murs en pierre, rénovations, extérieurs et comparaisons avant / après."
      path="/realisations"
    />
    <PageHero
      overline="Réalisations"
      title="Nos réalisations en Valais"
      intro="Un aperçu concret des interventions : entretien d'immeubles, extérieurs, rénovations et comparaisons avant / après. Chaque projet pourra prochainement être présenté en détail : problème rencontré, intervention réalisée et résultat."
    />
    <Realisations />
    <Related current="/realisations" />
  </>
);

export const AProposPage = () => (
  <>
    <Seo
      title="À propos — Steve Rosset, conciergerie & services à Sion, Valais"
      description="Steve Rosset, fondateur de Rosset Conciergerie & Services à Sion : un parcours polyvalent dans la construction, la rénovation, l'entretien et le paysagisme, au service des biens du Valais."
      path="/a-propos"
    />
    <PageHero
      overline="À propos"
      title="Steve Rosset — un interlocuteur qui connaît votre bien"
      intro="Derrière Rosset Conciergerie & Services, une entreprise de Rosset Solutions, il y a un parcours de terrain : construction, rénovation, entretien et paysagisme."
    />
    <About />
    <Related current="/a-propos" />
  </>
);

export const ContactPage = () => (
  <>
    <Seo
      title="Demande de devis gratuit — Rosset Conciergerie & Services, Sion"
      description="Demandez un devis gratuit pour une conciergerie PPE, un nettoyage, une haute pression ou des travaux à Sion et en Valais. Réponse personnelle de Steve Rosset : 078 638 02 03."
      path="/contact"
    />
    <PageHero
      overline="Contact & devis"
      title="Demander un devis gratuit en Valais"
      intro="Décrivez votre besoin en quelques lignes, ajoutez des photos si utile : vous recevrez une proposition claire et sans engagement, établie par Steve Rosset."
    />
    <ZoneIntervention />
    <DevisForm />
  </>
);

export const DebarrasPage = () => (
  <>
    <Seo
      title="Débarras à Sion et en Valais — caves, greniers, logements | Rosset Conciergerie"
      description="Débarras de caves, greniers, logements et locaux à Sion et en Valais central : tri, évacuation et remise en état, avec possibilité de nettoyage après débarras. Devis gratuit."
      path="/debarras"
    />
    <PageHero
      overline="Débarras & évacuation"
      title="Débarras de caves, greniers et logements à Sion et en Valais"
      intro="Un déménagement, une succession, une cave qui déborde ou un local à vider : Rosset Conciergerie & Services prend en charge le débarras et l'évacuation, simplement."
    />
    <Section title="Un débarras simple, sans multiplier les intervenants">
      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-2">
        <Reveal>
          <p className="text-base leading-relaxed text-nuit/70">
            Que ce soit pour une cave, un appartement, une maison, un atelier ou une halle,
            n'hésitez pas à nous contacter. Nous avons véhicules et remorques à disposition
            et faisons également appel à un service de bennes en cas de besoin.
          </p>
          <p className="mt-4 text-base leading-relaxed text-nuit/70">
            L'avantage d'un interlocuteur polyvalent : le débarras peut être suivi d'un
            nettoyage, d'une remise en état ou de petites réparations — sans devoir chercher
            une autre entreprise pour chaque étape. Devis gratuit et sans engagement.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <List items={[
            "Caves, appartements et maisons",
            "Ateliers, halles et locaux professionnels",
            "Greniers, dépendances et surfaces à vider",
            "Évacuation avec véhicules et remorques, bennes si nécessaire",
            "Nettoyage et remise en état après débarras, sur demande",
          ]} />
        </Reveal>
      </div>
    </Section>
    <Related current="/debarras" />
  </>
);
